/**
 * Pure helpers to order flashcards by their reading position in the document.
 *
 * Ordering rules:
 *  - PDF  → (startPage, startPageStartOffset). The offset is the character distance from the
 *           start of the page to the selection, so it reflects on-page reading order.
 *  - EPUB → (chapterIndex, chapterCharOffset, progression, startOffset). chapterIndex is resolved
 *           against the publication spine (reading order); chapterCharOffset is the chapter-global
 *           character offset of the selection — the precise within-chapter order, invariant to zoom
 *           and pagination, distinguishing two selections on the same rendered page; progression and
 *           startOffset are residual tiebreakers for legacy cards created before chapterCharOffset
 *           existed (page-level progression, then node-relative offset).
 *  - Flashcards with no position (created from the panel) sort last, by createdAt.
 *
 * No store/Delfino imports — kept pure so it is fully unit-testable.
 */

/**
 * Reads a flashcard's chapter progression, tolerating both the in-memory selection shape
 * (location.locations.progression) and the rehydrated/persisted shapes.
 * @param {Object} card - A flashcard instance
 * @returns {number|null} Progression in [0,1], or null when absent
 */
export function readProgression(card) {
    const candidates = [
        card?.location?.locations?.progression,
        card?.location?.selection?.locations?.progression,
        card?.epubR2Location?.locations?.progression,
    ];
    const progression = candidates.find(candidate => typeof candidate === 'number');
    return progression ?? null;
}

/**
 * Reads a flashcard's chapter-global character offset — the number of text characters from the
 * start of the chapter body to the start of the selection. This is the precise within-chapter
 * reading-order signal (invariant to zoom and pagination), tolerating the in-memory selection
 * shape (location.selection.chapterCharOffset) and the persisted/rehydrated shapes. Returns null
 * for legacy cards created before this field existed.
 * @param {Object} card - A flashcard instance
 * @returns {number|null} The chapter-global character offset, or null when absent
 */
export function readChapterCharOffset(card) {
    const candidates = [card?.location?.selection?.chapterCharOffset, card?.epubR2Location?.chapterCharOffset];
    const offset = candidates.find(candidate => typeof candidate === 'number');
    return offset ?? null;
}

/**
 * Resolves a flashcard's EPUB chapter to its index in the reading order (spine).
 * Exact href match first, then a contains() fallback (mirrors store.js spine matching).
 * @param {Object} card - A flashcard instance
 * @param {Array<{href: string}>} spine - Publication reading order
 * @returns {number} Spine index, or Number.MAX_SAFE_INTEGER when it cannot be resolved
 */
export function resolveChapterIndex(card, spine) {
    const chapter = card?.epubChapter || card?.location?.href || card?.epubR2Location?.href || '';
    if (!chapter || !Array.isArray(spine) || spine.length === 0) {
        return Number.MAX_SAFE_INTEGER;
    }

    const exactIndex = spine.findIndex(item => item.href === chapter);
    if (exactIndex !== -1) {
        return exactIndex;
    }

    const fuzzyIndex = spine.findIndex(
        item => item.href && (item.href.includes(chapter) || chapter.includes(item.href))
    );
    return fuzzyIndex === -1 ? Number.MAX_SAFE_INTEGER : fuzzyIndex;
}

/**
 * Builds the numeric ordering key for a flashcard.
 * @param {Object} card - A flashcard instance
 * @param {Array<{href: string}>} spine - Publication reading order (EPUB only)
 * @returns {{kind: 'pdf'|'epub', keys: number[]}|null} The key, or null when the card has no position
 */
export function readingPositionKey(card, spine) {
    if (card?.startPage != null) {
        return { kind: 'pdf', keys: [card.startPage, card.startPageStartOffset ?? 0] };
    }

    if (card?.epubChapter || card?.location || card?.epubR2Location) {
        const startOffset = card?.location?.selection?.startOffset ?? card?.epubR2Location?.startOffset ?? 0;
        return {
            kind: 'epub',
            keys: [
                resolveChapterIndex(card, spine),
                readChapterCharOffset(card) ?? Number.POSITIVE_INFINITY,
                readProgression(card) ?? Number.POSITIVE_INFINITY,
                startOffset,
            ],
        };
    }

    return null;
}

const ISO_DATE_TIME_WITHOUT_ZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * Reads a card's creation time. The host sends `Y-m-d H:i:s` in UTC without a zone. ECMAScript
 * leaves that format to each engine to parse, so it is read as the ISO `Y-m-dTH:i:s`, and ISO
 * reads a date-time without a zone in the device's local time, so it gets a `Z`. Read as local
 * time, the hour skipped by a daylight-saving change shifts one hour and can put a newer card
 * first. A text that already carries a zone (`Z` or `±hh:mm`) is read as it is.
 * @param {{createdAt?: string}} card - A flashcard or a notebook selection
 * @returns {number} Milliseconds, or +Infinity when missing or unreadable
 */
function readCreationTime(card) {
    const isoText = String(card?.createdAt ?? '').replace(' ', 'T');
    const utcText = ISO_DATE_TIME_WITHOUT_ZONE.test(isoText) ? `${isoText}Z` : isoText;
    const time = Date.parse(utcText);
    return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

/**
 * Stable fallback comparator for cards that tie on reading position (or have none).
 * Orders by createdAt ascending, then uuid, so the total order is deterministic. A card
 * without a readable createdAt sorts after the dated ones.
 * @param {Object} a - A flashcard or a notebook selection
 * @param {Object} b - A flashcard or a notebook selection
 * @returns {number}
 */
export function compareByCreatedAtThenUuid(a, b) {
    const timeA = readCreationTime(a);
    const timeB = readCreationTime(b);
    if (timeA !== timeB) {
        return timeA - timeB;
    }
    return String(a?.uuid).localeCompare(String(b?.uuid));
}

/**
 * Lexicographic comparison of two numeric reading-position keys. A key shorter than the
 * other reads as 0 in its missing positions.
 * @param {number[]} keysA - Reading-position key
 * @param {number[]} keysB - Reading-position key
 * @returns {number} Negative when keysA comes first, positive when keysB does, 0 on a tie
 */
export function compareReadingPositionKeys(keysA, keysB) {
    const length = Math.max(keysA.length, keysB.length);
    for (let index = 0; index < length; index++) {
        const valueA = keysA[index] ?? 0;
        const valueB = keysB[index] ?? 0;
        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }
    }

    return 0;
}

/**
 * Comparator that orders flashcards by reading position. Cards with no position sort last.
 * Defensive against a mixed PDF/EPUB set (never happens — one format per book — but stays
 * deterministic): PDF sorts before EPUB.
 * @param {Object} a - A flashcard instance
 * @param {Object} b - A flashcard instance
 * @param {Array<{href: string}>} spine - Publication reading order (EPUB only)
 * @returns {number}
 */
export function compareFlashcardsByReadingPosition(a, b, spine) {
    const keyA = readingPositionKey(a, spine);
    const keyB = readingPositionKey(b, spine);

    if (!keyA && !keyB) {
        return compareByCreatedAtThenUuid(a, b);
    }
    if (!keyA) {
        return 1;
    }
    if (!keyB) {
        return -1;
    }
    if (keyA.kind !== keyB.kind) {
        return keyA.kind === 'pdf' ? -1 : 1;
    }

    return compareReadingPositionKeys(keyA.keys, keyB.keys) || compareByCreatedAtThenUuid(a, b);
}

/**
 * Orders the given flashcards by reading position and returns the minimal set of changes for the
 * OWNED cards only — the {uuid, sortOrder} entries whose sortOrder differs from the new zero-based
 * index.
 *
 * Shared/subscribed cards stay in the ordering so they keep occupying their reading slot: an owned
 * card that follows a shared one is assigned the higher index, leaving the shared card's position
 * reserved until it is deleted. Shared cards are never emitted in `changed` — the host reorder is
 * owner-scoped.
 * @param {Object[]} flashcards - All flashcards to order (owned + shared)
 * @param {Array<{href: string}>} spine - Publication reading order (EPUB only)
 * @param {(card: Object) => boolean} [isOwned] - True when the card belongs to the user and may be
 *   reordered on the host. Defaults to treating every card as owned.
 * @returns {{ordered: Object[], changed: Array<{uuid: string, sortOrder: number}>}}
 */
export function assignReadingOrder(flashcards, spine, isOwned = () => true) {
    const ordered = [...flashcards].sort((a, b) => compareFlashcardsByReadingPosition(a, b, spine));
    const changed = [];
    ordered.forEach((card, index) => {
        if (isOwned(card) && card.sortOrder !== index) {
            changed.push({ uuid: card.uuid, sortOrder: index });
        }
    });
    return { ordered, changed };
}
